// i18n

// Initialize browser error monitoring before the application renders.
import './instrument';

// highlight
import 'reactflow/dist/style.css';

import './index.css';
// Registers a window 'error' capture that swallows the benign ResizeObserver
// loop notice, so react-error-overlay does not throw up a click-blocking modal
// (which otherwise traps you on a collapsed sidebar). Imported first so the
// listener is in place before anything renders.
import './utils/silenceResizeObserver';
// Moves every calendar in the portal — antd's and MUI's — to a Monday week
// start. Imported for its side effects, before any picker is constructed.
import './utils/weekStartsMonday';
import './utils/highlight';
// lightbox
/* eslint-disable import/no-unresolved */
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
// scroll bar
import 'simplebar/src/simplebar.css';
// lightbox
import 'react-image-lightbox/style.css';

// editor
import 'react-quill/dist/quill.snow.css';
import 'react-datepicker/dist/react-datepicker.css';
// slick-carousel
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// lazy image
import 'react-lazy-load-image-component/src/effects/blur.css';
import 'react-lazy-load-image-component/src/effects/opacity.css';
import 'react-lazy-load-image-component/src/effects/black-and-white.css';

import ReactDOM from 'react-dom';
import { createUploadLink } from 'apollo-upload-client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Provider as ReduxProvider } from 'react-redux';
import { PersistGate } from 'redux-persist/lib/integration/react';
// @mui
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { WebSocketLink } from '@apollo/client/link/ws';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  HttpLink,
  from,
  split
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
// redux
import { store, persistor } from './redux/store';
// contexts
import { SettingsProvider } from './contexts/SettingsContext';
import { CollapseDrawerProvider } from './contexts/CollapseDrawerContext';

// Check our docs
// https://docs-minimals.vercel.app/authentication/ts-version

import { AuthProvider } from './contexts/JWTContext';
// import { AuthProvider } from './contexts/Auth0Context';
// import { AuthProvider } from './contexts/FirebaseContext';
// import { AuthProvider } from './contexts/AwsCognitoContext';

//
import 'animate.css';

import App from './App';
import { configureNotificationSession } from './utils/notificationPush';
import reportWebVitals from './reportWebVitals';
import { CollapseSettingDrawerProvider } from './contexts/CollapseSettingDrawerContext';
import { registerDeployReloadHandler } from './utils/deployReload';
import { installBrowserTranslationDomGuard } from './utils/browserTranslationDomGuard';
import { supabase } from './utils/supabaseClient';
import { reportTimeoutLink } from './utils/reportTimeoutLink';

// Recover from stale-bundle (post-deploy) chunk failures that happen outside
// React's render cycle, so they don't leave a blank page.
registerDeployReloadHandler();

// Keeps every editor's link pop-up inside the editor. Loaded on demand so
// Quill stays out of the entry bundle: the module patches the Tooltip
// prototype, which reaches editors made before or after it arrives. After
// utils/highlight above, because Quill reads window.hljs when it loads. A
// failed chunk surfaces as an unhandled rejection, which the deploy-reload
// handler treats like any other stale chunk.
import('./utils/quillTooltipStaysInEditor');

// Browser page translation rewrites text nodes outside React. Make React 17's
// DOM updates tolerate those translated wrappers across every portal section.
installBrowserTranslationDomGuard(document.getElementById('root'));

// ----------------------------------------------------------------------

const wsLink = new WebSocketLink({
  uri: process.env.REACT_APP_APOLLO_WEBSOCKET_URL,

  options: {
    reconnect: true,
    // Attach the current Supabase access token on each (re)connect so
    // subscriptions authenticate the same way queries do.
    connectionParams: async () => {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      return { Authorization: token ? `Bearer ${token}` : '' };
    }
  }
});

let httpLink = createUploadLink({
  uri: process.env.REACT_APP_APOLLO_SERVER_URL
});

// Attach the Supabase access token (auto-refreshed by supabase-js) to every
// GraphQL request. setContext supports async, so we always send a fresh token.
const authLink = setContext(async (_, { headers }) => {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : ''
    }
  };
});

// On an authentication failure from the server, drop the Supabase session so
// the app falls back to the login screen instead of looping on a dead token.
// ACCOUNT_DISABLED is the same verdict from the other side: the token is
// valid, the account behind it is not (deactivated or deleted while this tab
// was open), so the session has to end here too.
const errorLink = onError(({ graphQLErrors }) => {
  if (
    graphQLErrors?.some(
      (e) =>
        e?.extensions?.code === 'UNAUTHENTICATED' ||
        e?.extensions?.code === 'ACCOUNT_DISABLED' ||
        /unauthenticated|invalid\/expired token/i.test(e?.message || '')
    )
  ) {
    supabase.auth.signOut();
  }
});

httpLink = from([errorLink, reportTimeoutLink, authLink, httpLink]);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  httpLink
);

const client = new ApolloClient({
  cache: new InMemoryCache({ addTypename: false }),
  link: splitLink
});

configureNotificationSession(client, async () => {
  wsLink.subscriptionClient.close(true, true);
  await client.clearStore();
});
const notifyRealtimeConnected = () => window.dispatchEvent(new Event('universe:realtime-connected'));
wsLink.subscriptionClient.onConnected(notifyRealtimeConnected);
wsLink.subscriptionClient.onReconnected(notifyRealtimeConnected);
window.addEventListener('universe:auth-refreshed', () => wsLink.subscriptionClient.close(true, true));

ReactDOM.render(
  <ApolloProvider client={client}>
    <AuthProvider>
      <HelmetProvider>
        <ReduxProvider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <SettingsProvider>
                <CollapseDrawerProvider>
                  <CollapseSettingDrawerProvider>
                    <BrowserRouter>
                      <App />
                    </BrowserRouter>
                  </CollapseSettingDrawerProvider>
                </CollapseDrawerProvider>
              </SettingsProvider>
            </LocalizationProvider>
          </PersistGate>
        </ReduxProvider>
      </HelmetProvider>
    </AuthProvider>
  </ApolloProvider>,
  document.getElementById('root')
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
// Push uses a dedicated, opt-in worker. Do not unregister it on page loads.

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
